import { memo, useMemo } from 'react'
import styled from 'styled-components'
import { dateDifference, getWeekdayNames, isDateBetween } from 'lib/datetime/dateUtils'
import { getSidebarCalendarMonthEmptyCells, getLocalDate } from 'lib/datetime/calendarUtils'
import type { AccommodationDayPickerSize, AccommodationDayView } from './AccommodationCalendar.types'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import Caption from 'LuxKit/Typography/Caption'
import type { AccommodationCalendarDayCellType, AccommodationDayCellVariant, AccommodationDayCellSelectHandler } from './AccommodationCalendarLargeDayCell'
import AccommodationCalendarLargeDayCell from './AccommodationCalendarLargeDayCell'
import AccommodationCalendarSmallDayCell from './AccommodationCalendarSmallDayCell'
import { rem } from 'polished'

const Gap8 = styled.div`
  height: ${rem(8)};
  grid-column: 1 / -1;
`
const weekDayData = getWeekdayNames()
// The standard order starts on sunday, we want to start on monday
// so we have to make our own array
const weekDays = [
  weekDayData.mon,
  weekDayData.tue,
  weekDayData.wed,
  weekDayData.thu,
  weekDayData.fri,
  weekDayData.sat,
  weekDayData.sun,
]

interface Props {
  days: Array<AccommodationDayView>
  onDaySelect?: AccommodationDayCellSelectHandler
  startDate?: string
  endDate?: string
  size: AccommodationDayPickerSize
}

function AccommodationCalendarDayMonth(props: Props) {
  const {
    days,
    startDate,
    endDate,
    onDaySelect,
    size,
  } = props

  const firstDate = useMemo(() => getLocalDate(days[0].date), [days])
  // our month may not start on the first day of the month, because we can't check in
  // on those days (they may be in the past). So we fill in the earliest weeks cells
  const emptyCells = useMemo(() => getSidebarCalendarMonthEmptyCells(firstDate.toISOString()), [firstDate])

  const firstCellStyle = useMemo(() => {
    return {
      // we need to align the first day of the month with our day heading
      // e.g. if the first day of a month is a wednesday, it should start under wednesday!
      gridColumnStart: firstDate.getDay() === 0 ? 7 : firstDate.getDay(), // Sunday goes to the end of the week
    }
  }, [firstDate])

  const duration = useMemo(() => {
    if (startDate && endDate) {
      return dateDifference(new Date(endDate), new Date(startDate)).days
    }
  }, [startDate, endDate])

  const [tier] = useCurrentLoyaltyTier()

  return (<>
    {weekDays.map(day => <Caption
      key={day.short}
      align={size === 'small' ? 'center' : 'start'}
      colour="neutral-three"
      weight="bold"
      variant="large"
      as="abbr"
      title={day.long}
    >
      {day.short}
    </Caption>)}
    <Gap8 />
    {size === 'small' && emptyCells.map((cell) => <AccommodationCalendarSmallDayCell
      key={cell.day}
      day={cell as any}
      style={{ gridColumnStart: cell.column }}
      variant="unavailable"
      type="none"
    />)}
    {size === 'large' && emptyCells.map((cell) => <AccommodationCalendarLargeDayCell
      key={cell.day}
      day={cell as any}
      style={{ gridColumnStart: cell.column }}
      variant="unavailable"
      type="none"
    />)}
    {days.map((day, index) => {
      let type: AccommodationCalendarDayCellType = 'none'
      const date = new Date(day.date)
      if (startDate && date.getTime() === new Date(startDate).getTime()) {
        type = 'check-in'
      } else if (endDate && date.getTime() === new Date(endDate).getTime()) {
        type = 'check-out'
      } else if (
        startDate &&
        endDate &&
        isDateBetween(new Date(startDate), new Date(endDate), date)
      ) {
        type = 'between'
      }

      let cellVariant: AccommodationDayCellVariant = 'none'
      if (day.pricing) {
        if (day.priceType === 'member') {
          cellVariant = 'price-member'
        } else {
          cellVariant = 'price'
        }
      } else if (day.soldOutExtraActionText) {
        cellVariant = 'sold-out-extra-action'
      } else if (day.soldOut) {
        cellVariant = 'unavailable'
      } else {
        cellVariant = 'none'
      }

      if (size === 'large') {
        return <AccommodationCalendarLargeDayCell
          key={+date}
          style={emptyCells.length === 0 && index === 0 ? firstCellStyle : undefined}
          day={day}
          onClick={onDaySelect}
          type={type}
          variant={cellVariant}
          tier={tier}
        />
      } else {
        return <AccommodationCalendarSmallDayCell
          key={+date}
          style={emptyCells.length === 0 && index === 0 ? firstCellStyle : undefined}
          day={day}
          onClick={onDaySelect}
          type={type}
          variant={cellVariant}
          duration={duration}
          tier={tier}
        />
      }
    })}
  </>
  )
}

export default memo(AccommodationCalendarDayMonth)
